import { ToolbarButton, useMenuContext_unstable } from '@fluentui/react-components';
import {
    ArchiveRegular,
    ArrowExportRegular,
    ArrowImportRegular,
    ArrowSyncRegular,
    DocumentAddRegular,
    DocumentCheckmarkRegular,
    DocumentCopyRegular,
    DocumentDismissRegular,
    DocumentProhibitedRegular,
    DocumentSyncRegular,
} from '@fluentui/react-icons';
import { useEffect, useRef, useState } from 'react';
import { useIsMounted } from 'usehooks-ts';
import { triggerConfirmationDialog } from '../dialogs/ConfirmationDialog';
import { DocToolbarLink } from '../documentation/DocToolbarLink';
import type { IQueryListProps } from '../list/QueryList';
import {
    triggerClearMessages,
    triggerErrorMessage,
    triggerSuccessMessage,
} from '../message-bar-group/MessageBarGroup';
import { confirmResetProcessDefinition } from '../process-definition/confirm-reset-process-definition';
import {
    cleanProcessDefinitionBeforeExport,
    type IResetProcessDefinitionResult,
    resetProcess,
    resetProcessDefinition,
} from '../process-definition/reset-process-definition';
import { ToolbarLink } from '../toolbar/ToolbarLink';
import { ToolbarOverflowGroup } from '../toolbar/ToolbarOverflowGroup';
import {
    processDefinitionsRouteApi,
    useProcessDefinitionsAnySelected,
    useProcessDefinitionsResetSelectionState,
    useProcessDefinitionsSelectionState,
} from './process-definitions-api';
import {
    useProcessDefinitionsChangeStatus,
    useProcessDefinitionsDelete,
    useProcessDefinitionsDeploy,
    useProcessDefinitionsQueryFragment,
    useProcessDefinitionsQueryRefresh,
    useProcessDefinitionsQueryXml,
} from './process-definitions-query';

const ButtonRefresh = () => {
    const refresh = useProcessDefinitionsQueryRefresh();
    return (
        <ToolbarButton
            aria-label="Liste mit Prozessdefinitionen aktualisieren"
            icon={<ArrowSyncRegular />}
            onClick={() => {
                triggerClearMessages();
                refresh();
            }}
            title="Liste aktualisieren"
        />
    );
};

const LinkCreate = () => (
    <ToolbarLink
        aria-label="Neue Prozessdefinition erstellen"
        icon={<DocumentAddRegular />}
        params={{ id: '-1' }}
        //@ts-ignore
        search={(prevSearch) => (Object.keys(prevSearch).length > 0 ? { prevSearch } : undefined)}
        to="/process-definition/$id"
    >
        Neu
    </ToolbarLink>
);

const itemDeactivate = new ToolbarOverflowGroup.Item(() => {
    const refresh = useProcessDefinitionsQueryRefresh();
    const [commit, isInFlight] = useProcessDefinitionsChangeStatus();
    const selectionState = useProcessDefinitionsSelectionState(
        useProcessDefinitionsQueryFragment(),
        ({ status }) => status === 'ACTIVE'
    );
    const resetSelectionState = useProcessDefinitionsResetSelectionState();

    return {
        'aria-label': 'Ausgewählte Prozessdefinitionen deaktivieren',
        children: 'Deaktivieren',
        disabled: !useProcessDefinitionsAnySelected(selectionState) || isInFlight,
        icon: <DocumentProhibitedRegular />,
        onClick: () => {
            triggerConfirmationDialog({
                confirmFn: () => {
                    triggerClearMessages();
                    const errTitle = 'Fehler beim Deaktivieren';
                    commit({
                        onCompleted: (commitResult, commitErrors) => {
                            if (commitErrors && commitErrors.length > 0) {
                                triggerErrorMessage(commitErrors, errTitle);
                                return;
                            }

                            if (commitResult.changeProcessDefinitionStatus === false) {
                                triggerErrorMessage(
                                    'Prozessdefinition(en) konnte(n) nicht deaktiviert werden',
                                    errTitle
                                );
                                return;
                            }

                            triggerSuccessMessage('Prozessdefinition(en) erfolgreich deaktiviert');
                            refresh();
                            resetSelectionState();
                        },
                        onError: (error) => {
                            triggerErrorMessage(error.message, errTitle);
                        },
                        variables: { ids: selectionState as string[], status: 'INACTIVE' },
                    });
                },
                confirmText: 'Deaktivieren',
                content: 'Möchten Sie die ausgewählten Prozessdefinitionen wirklich deaktivieren?',
                title: 'Prozessdefinition(en) deaktivieren',
            });
        },
    };
});

const itemReactivate = new ToolbarOverflowGroup.Item(() => {
    const refresh = useProcessDefinitionsQueryRefresh();
    const [commit, isInFlight] = useProcessDefinitionsChangeStatus();
    const selectionState = useProcessDefinitionsSelectionState(
        useProcessDefinitionsQueryFragment(),
        ({ status }) => status === 'INACTIVE'
    );
    const resetSelectionState = useProcessDefinitionsResetSelectionState();

    return {
        'aria-label': 'Ausgewählte Prozessdefinitionen reaktivieren',
        children: 'Reaktivieren',
        disabled: !useProcessDefinitionsAnySelected(selectionState) || isInFlight,
        icon: <DocumentSyncRegular />,
        onClick: () => {
            triggerConfirmationDialog({
                confirmFn: () => {
                    triggerClearMessages();
                    const errTitle = 'Fehler beim Reaktivieren';
                    commit({
                        onCompleted: (commitResult, commitErrors) => {
                            if (commitErrors && commitErrors.length > 0) {
                                triggerErrorMessage(commitErrors, errTitle);
                                return;
                            }

                            if (commitResult.changeProcessDefinitionStatus === false) {
                                triggerErrorMessage(
                                    'Prozessdefinition(en) konnte(n) nicht reaktiviert werden',
                                    errTitle
                                );
                                return;
                            }

                            triggerSuccessMessage('Prozessdefinition(en) erfolgreich reaktiviert');
                            refresh();
                            resetSelectionState();
                        },
                        onError: (error) => {
                            triggerErrorMessage(error.message, errTitle);
                        },
                        variables: { ids: selectionState as string[], status: 'ACTIVE' },
                    });
                },
                confirmText: 'Reaktivieren',
                content: 'Möchten Sie die ausgewählten Prozessdefinitionen wirklich reaktivieren?',
                title: 'Prozessdefinition(en) reaktivieren',
            });
        },
    };
});

const itemArchive = new ToolbarOverflowGroup.Item(() => {
    const refresh = useProcessDefinitionsQueryRefresh();
    const [commit, isInFlight] = useProcessDefinitionsChangeStatus();
    const selectionState = useProcessDefinitionsSelectionState(
        useProcessDefinitionsQueryFragment(),
        ({ status }) => status === 'INACTIVE'
    );
    const resetSelectionState = useProcessDefinitionsResetSelectionState();

    return {
        'aria-label': 'Ausgewählte Prozessdefinitionen archivieren',
        children: 'Archivieren',
        disabled: !useProcessDefinitionsAnySelected(selectionState) || isInFlight,
        icon: <ArchiveRegular />,
        onClick: () => {
            triggerConfirmationDialog({
                confirmFn: () => {
                    triggerClearMessages();
                    const errTitle = 'Fehler beim Archivieren';
                    commit({
                        onCompleted: (commitResult, commitErrors) => {
                            if (commitErrors && commitErrors.length > 0) {
                                triggerErrorMessage(commitErrors, errTitle);
                                return;
                            }

                            if (commitResult.changeProcessDefinitionStatus === false) {
                                triggerErrorMessage(
                                    'Prozessdefinition(en) konnte(n) nicht archiviert werden',
                                    errTitle
                                );
                                return;
                            }

                            triggerSuccessMessage('Prozessdefinition(en) erfolgreich archiviert');
                            refresh();
                            resetSelectionState();
                        },
                        onError: (error) => {
                            triggerErrorMessage(error.message, errTitle);
                        },
                        variables: { ids: selectionState as string[], status: 'ARCHIVED' },
                    });
                },
                confirmText: 'Archivieren',
                content: 'Möchten Sie die ausgewählten Prozessdefinitionen wirklich archivieren?',
                title: 'Prozessdefinition(en) archivieren',
            });
        },
    };
});

const itemPublish = new ToolbarOverflowGroup.Item(() => {
    const refresh = useProcessDefinitionsQueryRefresh();
    const [commit, isInFlight] = useProcessDefinitionsDeploy();
    const selectionState = useProcessDefinitionsSelectionState(
        useProcessDefinitionsQueryFragment(),
        (entry) => {
            let publishable;
            switch (entry.status) {
                case 'ACTIVE':
                case 'DRAFT':
                    publishable = true;
                    break;
                default:
                    publishable = false;
                    break;
            }
            return publishable && entry.dirtyFlag;
        }
    );
    const resetSelectionState = useProcessDefinitionsResetSelectionState();

    return {
        'aria-label': 'Ausgewählte Prozessdefinition veröffentlichen',
        children: 'Veröffentlichen',
        // disabled: true,
        disabled: selectionState?.length !== 1 || isInFlight,
        icon: <DocumentCheckmarkRegular />,
        onClick: () => {
            triggerConfirmationDialog({
                confirmFn: () => {
                    triggerClearMessages();
                    const errTitle = 'Fehler beim Veröffentlichen';
                    commit({
                        onCompleted: (commitResult, commitErrors) => {
                            if (commitErrors && commitErrors.length > 0) {
                                triggerErrorMessage(commitErrors, errTitle);
                                return;
                            }

                            if (commitResult.deployProcessDefinition === false) {
                                triggerErrorMessage(
                                    'Prozessdefinition konnte nicht veröffentlicht werden',
                                    errTitle
                                );
                                return;
                            }

                            triggerSuccessMessage('Prozessdefinition erfolgreich veröffentlicht');
                            refresh();
                            resetSelectionState();
                        },
                        onError: (error) => {
                            triggerErrorMessage(error.message, errTitle);
                        },
                        variables: { id: selectionState![0] as string },
                    });
                },
                confirmText: 'Veröffentlichen',
                content: 'Möchten Sie die ausgewählte Prozessdefinition wirklich veröffentlichen?',
                title: 'Prozessdefinition veröffentlichen',
            });
        },
    };
});

const itemDelete = new ToolbarOverflowGroup.Item(() => {
    const refresh = useProcessDefinitionsQueryRefresh();
    const [commit, isInFlight] = useProcessDefinitionsDelete();
    const selectionState = useProcessDefinitionsSelectionState(
        useProcessDefinitionsQueryFragment(),
        (entry) => entry.status !== 'ACTIVE'
    );
    const resetSelectionState = useProcessDefinitionsResetSelectionState();
    return {
        'aria-label': 'Ausgewählte Prozessdefinitionen löschen',
        children: 'Löschen',
        disabled:
            !selectionState || !useProcessDefinitionsAnySelected(selectionState) || isInFlight,
        icon: <DocumentDismissRegular />,
        onClick: () => {
            triggerConfirmationDialog({
                confirmFn: () => {
                    triggerClearMessages();
                    const errTitle = 'Fehler beim Löschen';
                    commit({
                        onCompleted: (commitResult, commitErrors) => {
                            if (commitErrors && commitErrors.length > 0) {
                                triggerErrorMessage(commitErrors, errTitle);
                                return;
                            }

                            if (commitResult.deleteProcessDefinitions === false) {
                                triggerErrorMessage(
                                    'Prozessdefinition(en) konnte(n) nicht gelöscht werden',
                                    errTitle
                                );
                                return;
                            }

                            triggerSuccessMessage('Prozessdefinition(en) erfolgreich gelöscht');
                            refresh();
                            resetSelectionState();
                        },
                        onError: (error) => {
                            triggerErrorMessage(error.message, errTitle);
                        },
                        variables: { ids: selectionState as string[] },
                    });
                },
                confirmText: 'Löschen',
                content: 'Möchten Sie die ausgewählten Prozessdefinitionen wirklich löschen?',
                title: 'Prozessdefinition(en) löschen',
            });
        },
    };
});

const itemCopy = new ToolbarOverflowGroup.Item(() => {
    const selectionState = useProcessDefinitionsSelectionState(
        useProcessDefinitionsQueryFragment()
    );
    const navigate = processDefinitionsRouteApi.useNavigate();
    const [busy, setBusy] = useState<boolean | void>();
    const loadXml = useProcessDefinitionsQueryXml();
    const mountRef = useRef(useIsMounted());
    // TODO: provide a link component
    return {
        'aria-label': 'Ausgewählte Prozessdefinition kopieren',
        children: 'Kopieren',
        disabled: busy || !selectionState || selectionState.length !== 1,
        icon: <DocumentCopyRegular />,
        onClick: () => {
            setBusy(true);
            triggerClearMessages();
            loadXml(selectionState![0] as string)
                .then((result) => {
                    if (!result || !result.definitionXml) {
                        throw new Error('XML-Dokument fehlt');
                    }
                    setBusy();
                    return new Promise<IResetProcessDefinitionResult>((resolve, reject) => {
                        confirmResetProcessDefinition((reset) => {
                            (reset
                                ? resetProcessDefinition(result.definitionXml)
                                : resetProcess(result.definitionXml)
                            )
                                .then(resolve)
                                .catch(reject);
                        });
                    });
                })
                .then((resetResult) => {
                    if (mountRef.current()) {
                        navigate({
                            params: { id: '-1' },
                            //@ts-ignore
                            search: (prevSearch) => ({ prevSearch }),
                            state: { xml: resetResult.xml } as any,
                            to: '/process-definition/$id',
                        });
                    }
                })
                .catch((error: Error) => {
                    console.log('Error', error);
                    triggerErrorMessage(error.message, 'Fehler beim Kopieren');
                    setBusy();
                });
        },
    };
});

const itemExport = new ToolbarOverflowGroup.Item(() => {
    const selectionState = useProcessDefinitionsSelectionState(
        useProcessDefinitionsQueryFragment()
    );
    const [busy, setBusy] = useState<boolean | void>();
    const loadXml = useProcessDefinitionsQueryXml();

    return {
        'aria-label': 'Ausgewählte Prozessdefinition exportieren',
        children: 'Exportieren',
        disabled: busy || !selectionState || selectionState.length !== 1,
        icon: <ArrowExportRegular />,
        onClick: () => {
            triggerClearMessages();
            setBusy(true);
            loadXml(selectionState![0] as string)
                .then((result) => {
                    if (!result || !result.definitionXml) {
                        throw new Error('XML-Dokument fehlt');
                    }
                    cleanProcessDefinitionBeforeExport(result.definitionXml, [
                        'historyTimeToLive',
                        'name',
                        'isExecutable',
                        'startPriority',
                    ]).then((xml) => {
                        // data
                        const blob = new Blob([xml], { type: 'application/xml' });
                        const url = URL.createObjectURL(blob);

                        // link
                        const link = document.createElement('a');
                        link.href = url;
                        link.download = `${result.definitionName} Prozessdefinition.xml`.trim();
                        link.style.display = 'none';
                        document.body.appendChild(link);
                        link.click();

                        // clean up
                        document.body.removeChild(link);
                        URL.revokeObjectURL(url);
                    });
                })
                .catch((error: Error) => {
                    triggerErrorMessage(error.message, 'Fehler beim Exportieren');
                })
                .finally(setBusy);
        },
    };
});

const itemImport = new ToolbarOverflowGroup.Item(() => {
    const navigate = processDefinitionsRouteApi.useNavigate();
    const iptRef = useRef<HTMLInputElement>(null);
    const setOpen = useMenuContext_unstable((context) => context.setOpen);
    const setOpenRef = useRef<(event: any, open?: boolean) => void>(null);
    setOpenRef.current = (event, open = false) => {
        setOpen(event, {
            event,
            open,
            type: 'menuItemClick',
        });
    };

    useEffect(() => {
        const input = iptRef.current;

        if (input) {
            const onCancel = (event: Event) => {
                setOpenRef.current!(event);
            };

            input.addEventListener('cancel', onCancel);

            return () => {
                input.removeEventListener('cancel', onCancel);
            };
        }
    }, []);

    return {
        'aria-label': 'Prozessdefinition importieren',
        children: (
            <>
                Importieren
                <input
                    accept=".xml"
                    onChange={(event) => {
                        const file = (event.target.files || [])[0];
                        setOpenRef.current!(event);
                        if (!file) {
                            return;
                        }

                        event.target.value = '';

                        confirmResetProcessDefinition((reset) => {
                            triggerClearMessages();

                            file.text()
                                .then((xml) =>
                                    reset
                                        ? resetProcessDefinition(xml, [
                                              'historyTimeToLive',
                                              'name',
                                              'isExecutable',
                                              'startPriority',
                                          ])
                                        : resetProcess(xml)
                                )
                                .then((resetResult) => {
                                    navigate({
                                        params: { id: '-1' },
                                        //@ts-ignore
                                        search: (prevSearch) => ({ prevSearch }),
                                        state: { xml: resetResult.xml } as any,
                                        to: '/process-definition/$id',
                                    });
                                })
                                .catch((error: Error) => {
                                    triggerErrorMessage(error.message, 'Fehler beim Importieren');
                                });
                        });
                    }}
                    ref={iptRef}
                    style={{ display: 'none' }}
                    type="file"
                />
            </>
        ) as any,
        // disabled: !selectionState || selectionState.length !== 1,
        icon: <ArrowImportRegular />,
        onClick: () => {
            iptRef.current?.click();
        },
        persistOnClick: true,
    };
});

export const toolbarItems: IQueryListProps['toolbar'] = {
    farItems: [<DocToolbarLink key="dl" />],
    items: [<ButtonRefresh key="br" />, <LinkCreate key="lc" />],
    overflowItems: [
        itemCopy,
        itemPublish,
        itemDeactivate,
        itemReactivate,
        itemArchive,
        itemExport,
        itemImport,
        itemDelete,
    ],
} as const;
